/**
 * User Profile Component
 * Manages user profile settings, API tokens, and GitHub organizations
 */

import React, { useState, useEffect } from 'react';

const UserProfile = ({ apiService }) => {
  // State management
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [apiTokens, setApiTokens] = useState([]);
  const [githubOrganizations, setGithubOrganizations] = useState([]);
  const [azureDevOpsOrganizations, setAzureDevOpsOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    notification_email: '',
    webhook_url: '',
    slack_webhook: '',
    teams_webhook: '',
    notification_channels: ['email', 'in_app']
  });

  // API token form state
  const [tokenForm, setTokenForm] = useState({
    name: '',
    token_type: 'ci_cd',
    description: '',
    expires_days: '',
    scopes: []
  });

  // GitHub organization form state
  const [githubForm, setGithubForm] = useState({
    name: '',
    organization_url: '',
    github_token: '',
    frequency: 'weekly',
    custom_cron: '',
    max_repositories: 50,
    notification_threshold: 3,
    auto_threat_model: true,
    notification_level: 'medium'
  });

  // Azure DevOps organization form state
  const [azureDevOpsForm, setAzureDevOpsForm] = useState({
    name: '',
    organization_url: '',
    pat_token: '',
    frequency: 'weekly',
    custom_cron: '',
    max_repositories: 50,
    max_projects: 20,
    notification_threshold: 3,
    auto_threat_model: true,
    notification_level: 'all',
    scan_pipelines: true,
    scan_arm_templates: true,
    scan_bicep: true
  });

  const [showTokenForm, setShowTokenForm] = useState(false);
  const [showGithubForm, setShowGithubForm] = useState(false);
  const [showAzureDevOpsForm, setShowAzureDevOpsForm] = useState(false);
  const [editingGithub, setEditingGithub] = useState(null);
  const [editingAzureDevOps, setEditingAzureDevOps] = useState(null);
  const [createdToken, setCreatedToken] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load initial data
  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError('');

      const [profileData, tokensData, orgsData, azureOrgsData] = await Promise.all([
        apiService.getUserProfile(),
        apiService.getApiTokens(),
        apiService.getGithubOrganizations(),
        apiService.getAzureDevOpsOrganizations()
      ]);

      setProfile(profileData);
      setProfileForm({
        name: profileData.name || '',
        notification_email: profileData.notification_email || '',
        webhook_url: profileData.webhook_url || '',
        slack_webhook: profileData.slack_webhook || '',
        teams_webhook: profileData.teams_webhook || '',
        notification_channels: profileData.notification_channels || ['email', 'in_app']
      });

      setApiTokens(tokensData);
      setGithubOrganizations(orgsData.organizations || []);
      setAzureDevOpsOrganizations(azureOrgsData.organizations || []);

    } catch (err) {
      setError('Failed to load profile data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      await apiService.updateUserProfile(profileForm);
      setSuccess('Profile updated successfully!');

      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateApiToken = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      const tokenData = {
        ...tokenForm,
        expires_days: tokenForm.expires_days ? parseInt(tokenForm.expires_days) : null
      };

      const response = await apiService.createApiToken(tokenData);
      setCreatedToken(response);
      setShowTokenForm(false);
      setTokenForm({
        name: '',
        token_type: 'ci_cd',
        description: '',
        expires_days: '',
        scopes: []
      });

      await loadProfileData();
    } catch (err) {
      setError('Failed to create API token: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      await apiService.revokeApiToken(tokenId);
      setSuccess('API token revoked successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError('Failed to revoke token: ' + err.message);
    }
  };

  const handleCreateGithubOrg = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      if (editingGithub) {
        await apiService.updateGithubOrganization(editingGithub.id, githubForm);
        setSuccess('GitHub organization updated successfully!');
      } else {
        await apiService.createGithubOrganization(githubForm);
        setSuccess('GitHub organization added successfully!');
      }

      setShowGithubForm(false);
      setEditingGithub(null);
      setGithubForm({
        name: '',
        organization_url: '',
        github_token: '',
        frequency: 'weekly',
        custom_cron: '',
        max_repositories: 50,
        notification_threshold: 3,
        auto_threat_model: true,
        notification_level: 'medium'
      });

      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError(`Failed to ${editingGithub ? 'update' : 'create'} GitHub organization: ` + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGithubOrg = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this GitHub organization? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      await apiService.deleteGithubOrganization(orgId);
      setSuccess('GitHub organization deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError('Failed to delete GitHub organization: ' + err.message);
    }
  };

  const editGithubOrg = (org) => {
    setEditingGithub(org);
    setGithubForm({
      name: org.name,
      organization_url: org.url,
      github_token: '', // Don't pre-fill for security
      frequency: org.scan_frequency,
      custom_cron: org.custom_cron || '',
      max_repositories: org.max_repositories,
      notification_threshold: org.notification_threshold,
      auto_threat_model: org.auto_threat_model,
      notification_level: org.notification_level
    });
    setShowGithubForm(true);
  };

  // Azure DevOps organization handlers
  const handleCreateAzureDevOpsOrg = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      if (editingAzureDevOps) {
        await apiService.updateAzureDevOpsOrganization(editingAzureDevOps.id, azureDevOpsForm);
        setSuccess('Azure DevOps organization updated successfully!');
      } else {
        await apiService.createAzureDevOpsOrganization(azureDevOpsForm);
        setSuccess('Azure DevOps organization added successfully!');
      }

      setShowAzureDevOpsForm(false);
      setEditingAzureDevOps(null);
      setAzureDevOpsForm({
        name: '',
        organization_url: '',
        pat_token: '',
        frequency: 'weekly',
        custom_cron: '',
        max_repositories: 50,
        max_projects: 20,
        notification_threshold: 3,
        auto_threat_model: true,
        notification_level: 'all',
        scan_pipelines: true,
        scan_arm_templates: true,
        scan_bicep: true
      });

      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError(`Failed to ${editingAzureDevOps ? 'update' : 'create'} Azure DevOps organization: ` + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAzureDevOpsOrg = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this Azure DevOps organization? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      await apiService.deleteAzureDevOpsOrganization(orgId);
      setSuccess('Azure DevOps organization deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfileData();
    } catch (err) {
      setError('Failed to delete Azure DevOps organization: ' + err.message);
    }
  };

  const editAzureDevOpsOrg = (org) => {
    setEditingAzureDevOps(org);
    setAzureDevOpsForm({
      name: org.name,
      organization_url: org.organization_url,
      pat_token: '', // Don't pre-fill for security
      frequency: org.scan_frequency,
      custom_cron: org.custom_cron || '',
      max_repositories: org.max_repositories,
      max_projects: org.max_projects,
      notification_threshold: org.notification_threshold,
      auto_threat_model: org.auto_threat_model,
      notification_level: org.notification_level,
      scan_pipelines: org.scan_pipelines,
      scan_arm_templates: org.scan_arm_templates,
      scan_bicep: org.scan_bicep
    });
    setShowAzureDevOpsForm(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="user-profile loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="user-profile-header">
        <h1>👤 User Profile</h1>
        <div className="profile-summary">
          <div className="profile-info">
            <span className="profile-name">{profile?.name}</span>
            <span className="profile-email">{profile?.email}</span>
          </div>
        </div>
      </div>

      <div className="user-profile-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile Settings
        </button>
        <button
          className={`tab ${activeTab === 'tokens' ? 'active' : ''}`}
          onClick={() => setActiveTab('tokens')}
        >
          🔑 API Tokens
          {apiTokens.length > 0 && (
            <span className="token-count">{apiTokens.length}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'github' ? 'active' : ''}`}
          onClick={() => setActiveTab('github')}
        >
          🏢 GitHub Organizations
          {githubOrganizations.length > 0 && (
            <span className="org-count">{githubOrganizations.length}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'azuredevops' ? 'active' : ''}`}
          onClick={() => setActiveTab('azuredevops')}
        >
          ☁️ Azure DevOps
          {azureDevOpsOrganizations.length > 0 && (
            <span className="org-count">{azureDevOpsOrganizations.length}</span>
          )}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      {/* Created Token Modal */}
      {createdToken && (
        <div className="modal-overlay" onClick={() => setCreatedToken(null)}>
          <div className="modal-content token-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🔑 API Token Created</h3>
            <p className="token-warning">
              ⚠️ <strong>Important:</strong> This token will only be shown once. Copy it now and store it securely.
            </p>
            <div className="created-token-display">
              <div className="token-details">
                <strong>Name:</strong> {createdToken.token.name}<br/>
                <strong>Type:</strong> {createdToken.token.token_type}<br/>
                <strong>Expires:</strong> {createdToken.token.expires_at ? formatTimestamp(createdToken.token.expires_at) : 'Never'}
              </div>
              <div className="token-value">
                <label>Your API Token:</label>
                <code>{createdToken.raw_token}</code>
                <button
                  className="btn-copy"
                  onClick={() => copyToClipboard(createdToken.raw_token)}
                >
                  📋 Copy
                </button>
              </div>
              <div className="token-value api-endpoint">
                <label>API Base URL:</label>
                <code>{window.location.origin}/api</code>
                <button
                  className="btn-copy"
                  onClick={() => copyToClipboard(`${window.location.origin}/api`)}
                >
                  📋 Copy
                </button>
              </div>
            </div>
            <div className="ci-cd-usage">
              <h4>CI/CD Usage Example:</h4>
              <pre className="code-block">
{`# Set environment variables
export THREATMODELER_API_URL="${window.location.origin}/api"
export THREATMODELER_API_TOKEN="${createdToken.raw_token}"

# Example API call
curl -X POST "$THREATMODELER_API_URL/threat-model" \\
  -H "Authorization: Bearer $THREATMODELER_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"description": "Your system description"}'`}
              </pre>
              <button
                className="btn-copy"
                onClick={() => copyToClipboard(`# Set environment variables
export THREATMODELER_API_URL="${window.location.origin}/api"
export THREATMODELER_API_TOKEN="${createdToken.raw_token}"

# Example API call
curl -X POST "$THREATMODELER_API_URL/threat-model" \\
  -H "Authorization: Bearer $THREATMODELER_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"description": "Your system description"}'`)}
              >
                📋 Copy Example
              </button>
            </div>
            <div className="token-modal-actions">
              <button className="btn-primary" onClick={() => setCreatedToken(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Settings Tab */}
      {activeTab === 'profile' && (
        <div className="profile-settings">
          <h2>Profile Settings</h2>

          <form onSubmit={handleProfileUpdate} className="profile-form">
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your display name"
                required
              />
            </div>

            <div className="form-group">
              <label>Notification Email</label>
              <input
                type="email"
                value={profileForm.notification_email}
                onChange={(e) => setProfileForm(prev => ({ ...prev, notification_email: e.target.value }))}
                placeholder="email@example.com"
              />
              <small>Email for receiving threat detection alerts</small>
            </div>

            <div className="form-group">
              <label>Webhook URL</label>
              <input
                type="url"
                value={profileForm.webhook_url}
                onChange={(e) => setProfileForm(prev => ({ ...prev, webhook_url: e.target.value }))}
                placeholder="https://your-webhook.com/alerts"
              />
            </div>

            <div className="form-group">
              <label>Slack Webhook URL</label>
              <input
                type="url"
                value={profileForm.slack_webhook}
                onChange={(e) => setProfileForm(prev => ({ ...prev, slack_webhook: e.target.value }))}
                placeholder="https://hooks.slack.com/services/..."
              />
            </div>

            <div className="form-group">
              <label>Teams Webhook URL</label>
              <input
                type="url"
                value={profileForm.teams_webhook}
                onChange={(e) => setProfileForm(prev => ({ ...prev, teams_webhook: e.target.value }))}
                placeholder="https://outlook.office.com/webhook/..."
              />
            </div>

            <div className="form-group">
              <label>Notification Channels</label>
              <div className="checkbox-group">
                {['email', 'webhook', 'in_app', 'slack', 'teams'].map(channel => (
                  <label key={channel} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={profileForm.notification_channels.includes(channel)}
                      onChange={(e) => {
                        const channels = e.target.checked
                          ? [...profileForm.notification_channels, channel]
                          : profileForm.notification_channels.filter(c => c !== channel);
                        setProfileForm(prev => ({ ...prev, notification_channels: channels }));
                      }}
                    />
                    <span className="channel-name">
                      {channel.charAt(0).toUpperCase() + channel.slice(1).replace('_', ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? '⏳ Saving...' : '💾 Save Profile'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* API Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className="api-tokens">
          <div className="tokens-header">
            <h2>API Tokens</h2>
            <button
              className="btn-primary"
              onClick={() => setShowTokenForm(true)}
            >
              🔑 Create Token
            </button>
          </div>

          {showTokenForm && (
            <div className="token-form-section">
              <h3>Create New API Token</h3>
              <form onSubmit={handleCreateApiToken} className="token-form">
                <div className="form-group">
                  <label>Token Name</label>
                  <input
                    type="text"
                    value={tokenForm.name}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="CI/CD Pipeline Token"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Token Type</label>
                  <select
                    value={tokenForm.token_type}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, token_type: e.target.value }))}
                  >
                    <option value="ci_cd">CI/CD Pipeline</option>
                    <option value="webhook">Webhook Integration</option>
                    <option value="integration">General Integration</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={tokenForm.description}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Description of what this token will be used for..."
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Expiration (Optional)</label>
                  <select
                    value={tokenForm.expires_days}
                    onChange={(e) => setTokenForm(prev => ({ ...prev, expires_days: e.target.value }))}
                  >
                    <option value="">Never expires</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowTokenForm(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? '⏳ Creating...' : '🔑 Create Token'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="tokens-list">
            {apiTokens.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔑</div>
                <h3>No API tokens yet</h3>
                <p>Create tokens for CI/CD pipelines and integrations.</p>
              </div>
            ) : (
              <div className="tokens-grid">
                {apiTokens.map((token) => (
                  <div key={token.id} className="token-card">
                    <div className="token-header">
                      <h4>{token.name}</h4>
                      <span className={`token-type ${token.token_type}`}>
                        {token.token_type.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="token-details">
                      {token.description && (
                        <p className="token-description">{token.description}</p>
                      )}

                      <div className="token-meta">
                        <div className="meta-item">
                          <strong>Created:</strong> {formatTimestamp(token.created_at)}
                        </div>
                        {token.expires_at && (
                          <div className="meta-item">
                            <strong>Expires:</strong> {formatTimestamp(token.expires_at)}
                          </div>
                        )}
                        {token.last_used_at && (
                          <div className="meta-item">
                            <strong>Last Used:</strong> {formatTimestamp(token.last_used_at)}
                          </div>
                        )}
                      </div>

                      {token.scopes.length > 0 && (
                        <div className="token-scopes">
                          <strong>Scopes:</strong> {token.scopes.join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="token-actions">
                      <button
                        className="btn-danger"
                        onClick={() => handleRevokeToken(token.id)}
                      >
                        🗑️ Revoke
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* GitHub Organizations Tab */}
      {activeTab === 'github' && (
        <div className="github-organizations">
          <div className="github-header">
            <h2>GitHub Organizations</h2>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingGithub(null);
                setGithubForm({
                  name: '',
                  organization_url: '',
                  github_token: '',
                  frequency: 'weekly',
                  custom_cron: '',
                  max_repositories: 50,
                  notification_threshold: 3,
                  auto_threat_model: true,
                  notification_level: 'medium'
                });
                setShowGithubForm(true);
              }}
            >
              🏢 Add Organization
            </button>
          </div>

          {showGithubForm && (
            <div className="github-form-section">
              <h3>{editingGithub ? 'Edit GitHub Organization' : 'Add GitHub Organization'}</h3>
              <form onSubmit={handleCreateGithubOrg} className="github-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Name</label>
                    <input
                      type="text"
                      value={githubForm.name}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="My Organization"
                      required
                    />
                    <small>Display name for this organization</small>
                  </div>

                  <div className="form-group">
                    <label>Organization URL</label>
                    <input
                      type="url"
                      value={githubForm.organization_url}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, organization_url: e.target.value }))}
                      placeholder="https://github.com/myorg"
                      required
                    />
                    <small>GitHub organization URL</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>GitHub Personal Access Token</label>
                  <input
                    type="password"
                    value={githubForm.github_token}
                    onChange={(e) => setGithubForm(prev => ({ ...prev, github_token: e.target.value }))}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                    required
                  />
                  <small>Token will be stored securely using SHA-256 hashing</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Scan Frequency</label>
                    <select
                      value={githubForm.frequency}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, frequency: e.target.value }))}
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom (Cron)</option>
                    </select>
                  </div>

                  {githubForm.frequency === 'custom' && (
                    <div className="form-group">
                      <label>Custom Cron Expression</label>
                      <input
                        type="text"
                        value={githubForm.custom_cron}
                        onChange={(e) => setGithubForm(prev => ({ ...prev, custom_cron: e.target.value }))}
                        placeholder="0 0 * * 0"
                      />
                      <small>Use standard cron format</small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Max Repositories</label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={githubForm.max_repositories}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, max_repositories: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Detection Threshold</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={githubForm.notification_threshold}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, notification_threshold: parseInt(e.target.value) }))}
                    />
                    <small>Minimum detections to trigger notification</small>
                  </div>

                  <div className="form-group">
                    <label>Notification Level</label>
                    <select
                      value={githubForm.notification_level}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, notification_level: e.target.value }))}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={githubForm.auto_threat_model}
                      onChange={(e) => setGithubForm(prev => ({ ...prev, auto_threat_model: e.target.checked }))}
                    />
                    Auto-trigger threat modeling on detections
                  </label>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowGithubForm(false);
                      setEditingGithub(null);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? '⏳ Saving...' : (editingGithub ? '💾 Update' : '➕ Add Organization')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="github-organizations-list">
            {githubOrganizations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏢</div>
                <h3>No GitHub organizations yet</h3>
                <p>Add GitHub organizations to scan for MCP development and compliance issues.</p>
              </div>
            ) : (
              <div className="organizations-grid">
                {githubOrganizations.map((org) => (
                  <div key={org.id} className="organization-card">
                    <div className="org-header">
                      <h4>{org.name}</h4>
                      <span className={`org-status ${org.is_active ? 'active' : 'inactive'}`}>
                        {org.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="org-details">
                      <div className="org-url">
                        <strong>URL</strong>
                        <a href={org.url} target="_blank" rel="noopener noreferrer">{org.url}</a>
                      </div>

                      <div className="org-meta">
                        <div className="meta-item">
                          <strong>Frequency</strong>
                          {org.scan_frequency}
                        </div>
                        <div className="meta-item">
                          <strong>Max Repos</strong>
                          {org.max_repositories || 50}
                        </div>
                        <div className="meta-item">
                          <strong>Last Scan</strong>
                          {org.last_scan_at ? formatTimestamp(org.last_scan_at) : 'Never'}
                        </div>
                        <div className="meta-item">
                          <strong>Scans</strong>
                          {org.scan_count || 0}
                        </div>
                      </div>
                    </div>

                    <div className="org-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => editGithubOrg(org)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteGithubOrg(org.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Azure DevOps Organizations Tab */}
      {activeTab === 'azuredevops' && (
        <div className="azure-devops-organizations">
          <div className="azure-devops-header">
            <h2>Azure DevOps Organizations</h2>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingAzureDevOps(null);
                setAzureDevOpsForm({
                  name: '',
                  organization_url: '',
                  pat_token: '',
                  frequency: 'weekly',
                  custom_cron: '',
                  max_repositories: 50,
                  max_projects: 20,
                  notification_threshold: 3,
                  auto_threat_model: true,
                  notification_level: 'all',
                  scan_pipelines: true,
                  scan_arm_templates: true,
                  scan_bicep: true
                });
                setShowAzureDevOpsForm(true);
              }}
            >
              ☁️ Add Organization
            </button>
          </div>

          {showAzureDevOpsForm && (
            <div className="azure-devops-form-section">
              <h3>{editingAzureDevOps ? 'Edit Azure DevOps Organization' : 'Add Azure DevOps Organization'}</h3>
              <form onSubmit={handleCreateAzureDevOpsOrg} className="azure-devops-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Name</label>
                    <input
                      type="text"
                      value={azureDevOpsForm.name}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="My Azure DevOps Org"
                      required
                    />
                    <small>Display name for this organization</small>
                  </div>

                  <div className="form-group">
                    <label>Organization URL</label>
                    <input
                      type="url"
                      value={azureDevOpsForm.organization_url}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, organization_url: e.target.value }))}
                      placeholder="https://dev.azure.com/myorg"
                      required
                    />
                    <small>Azure DevOps organization URL</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Personal Access Token (PAT)</label>
                  <input
                    type="password"
                    value={azureDevOpsForm.pat_token}
                    onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, pat_token: e.target.value }))}
                    placeholder="Enter your Azure DevOps PAT"
                    required={!editingAzureDevOps}
                  />
                  <small>PAT will be stored securely using SHA-256 hashing. {editingAzureDevOps && 'Leave blank to keep existing PAT.'}</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Scan Frequency</label>
                    <select
                      value={azureDevOpsForm.frequency}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, frequency: e.target.value }))}
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom (Cron)</option>
                    </select>
                  </div>

                  {azureDevOpsForm.frequency === 'custom' && (
                    <div className="form-group">
                      <label>Custom Cron Expression</label>
                      <input
                        type="text"
                        value={azureDevOpsForm.custom_cron}
                        onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, custom_cron: e.target.value }))}
                        placeholder="0 0 * * 0"
                      />
                      <small>Use standard cron format</small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Max Projects</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={azureDevOpsForm.max_projects}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, max_projects: parseInt(e.target.value) }))}
                    />
                  </div>

                  <div className="form-group">
                    <label>Max Repositories</label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={azureDevOpsForm.max_repositories}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, max_repositories: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Detection Threshold</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={azureDevOpsForm.notification_threshold}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, notification_threshold: parseInt(e.target.value) }))}
                    />
                    <small>Minimum detections to trigger notification</small>
                  </div>

                  <div className="form-group">
                    <label>Notification Level</label>
                    <select
                      value={azureDevOpsForm.notification_level}
                      onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, notification_level: e.target.value }))}
                    >
                      <option value="all">All Notifications</option>
                      <option value="high_only">High Priority Only</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                </div>

                <div className="form-group scan-options">
                  <label>Scan Options</label>
                  <div className="checkbox-group scan-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={azureDevOpsForm.scan_pipelines}
                        onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, scan_pipelines: e.target.checked }))}
                      />
                      <span className="checkbox-text">Scan Azure Pipelines</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={azureDevOpsForm.scan_arm_templates}
                        onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, scan_arm_templates: e.target.checked }))}
                      />
                      <span className="checkbox-text">Scan ARM Templates</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={azureDevOpsForm.scan_bicep}
                        onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, scan_bicep: e.target.checked }))}
                      />
                      <span className="checkbox-text">Scan Bicep Files</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={azureDevOpsForm.auto_threat_model}
                        onChange={(e) => setAzureDevOpsForm(prev => ({ ...prev, auto_threat_model: e.target.checked }))}
                      />
                      <span className="checkbox-text">Auto-trigger threat modeling on detections</span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowAzureDevOpsForm(false);
                      setEditingAzureDevOps(null);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? '⏳ Saving...' : (editingAzureDevOps ? '💾 Update' : '➕ Add Organization')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="azure-devops-organizations-list">
            {azureDevOpsOrganizations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">☁️</div>
                <h3>No Azure DevOps organizations yet</h3>
                <p>Add Azure DevOps organizations to scan for MCP development, compliance issues, and Azure security misconfigurations.</p>
              </div>
            ) : (
              <div className="organizations-grid">
                {azureDevOpsOrganizations.map((org) => (
                  <div key={org.id} className="organization-card azure-devops-card">
                    <div className="org-header">
                      <h4>{org.name}</h4>
                      <span className={`org-status ${org.is_active ? 'active' : 'inactive'}`}>
                        {org.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="org-details">
                      <div className="org-url">
                        <strong>URL</strong>
                        <a href={org.organization_url} target="_blank" rel="noopener noreferrer">{org.organization_url}</a>
                      </div>

                      <div className="org-meta">
                        <div className="meta-item">
                          <strong>Frequency</strong>
                          {org.scan_frequency}
                        </div>
                        <div className="meta-item">
                          <strong>Max Projects</strong>
                          {org.max_projects || 20}
                        </div>
                        <div className="meta-item">
                          <strong>Max Repos</strong>
                          {org.max_repositories || 50}
                        </div>
                        <div className="meta-item">
                          <strong>Last Scan</strong>
                          {org.last_scan_at ? formatTimestamp(org.last_scan_at) : 'Never'}
                        </div>
                        <div className="meta-item">
                          <strong>Scans</strong>
                          {org.scan_count || 0}
                        </div>
                      </div>

                      <div className="org-scan-options">
                        {org.scan_pipelines && <span className="scan-badge">Pipelines</span>}
                        {org.scan_arm_templates && <span className="scan-badge">ARM</span>}
                        {org.scan_bicep && <span className="scan-badge">Bicep</span>}
                      </div>
                    </div>

                    <div className="org-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => editAzureDevOpsOrg(org)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteAzureDevOpsOrg(org.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;